// 6-axis radar showing the user's score vs a peer baseline. Used on the
// /pricing teaser to dramatize "you're different from average". The dashboard
// uses its own (single-polygon) CapboiRadar.

import { REPORT_AXES, type ReportRadarValues } from '../dashboard/reportTypes';

interface RadarChartProps {
  user: ReportRadarValues;
  peer: ReportRadarValues;
  /** Axis labels to highlight, e.g. ["analytical", "pragmatic"]. */
  highlight?: string[];
  size?: number;
}

const LABELS: Record<keyof ReportRadarValues, string> = {
  communal: 'Communal',
  analytical: 'Analytical',
  pragmatic: 'Pragmatic',
  bold: 'Bold',
  orthodox: 'Orthodox',
  imaginative: 'Imaginative',
};

export function RadarChart({ user, peer, highlight = [], size = 320 }: RadarChartProps) {
  const cx = size / 2;
  const cy = size / 2;
  const r = size * 0.36;
  const labelR = size * 0.46;
  const N = REPORT_AXES.length;
  // Horizontal room for the longest side labels ("Imaginative", "Analytical")
  // — without this they overflow the square viewBox and get clipped.
  const padX = size * 0.26;

  const axis = (i: number) => {
    const a = -Math.PI / 2 + (i * 2 * Math.PI) / N;
    return { x: Math.cos(a), y: Math.sin(a) };
  };

  const polygon = (values: ReportRadarValues): string => {
    const pts = REPORT_AXES.map((k, i) => {
      const { x, y } = axis(i);
      const v = values[k] / 100;
      return `${cx + x * r * v},${cy + y * r * v}`;
    });
    return pts.join(' ');
  };

  return (
    <svg viewBox={`${-padX} 0 ${size + padX * 2} ${size}`} className="w-full max-w-[460px] h-auto" aria-label="Personality radar">
      {/* Concentric grid circles */}
      {[0.25, 0.5, 0.75, 1].map((lvl, i) => (
        <circle key={i} cx={cx} cy={cy} r={r * lvl} fill="none" stroke="rgba(16,24,40,0.1)" strokeWidth={i === 3 ? 1 : 0.5} strokeDasharray={i < 3 ? '2 3' : undefined} />
      ))}
      {/* Axis lines */}
      {REPORT_AXES.map((_, i) => {
        const { x, y } = axis(i);
        return (
          <line key={i} x1={cx} y1={cy} x2={cx + x * r} y2={cy + y * r} stroke="rgba(16,24,40,0.1)" strokeWidth="0.5" />
        );
      })}

      {/* Peer polygon (subtle) */}
      <polygon points={polygon(peer)} fill="rgba(252,165,165,0.18)" stroke="#fca5a5" strokeWidth="2" />
      {/* User polygon (bold teal) */}
      <polygon points={polygon(user)} fill="rgba(91,234,208,0.32)" stroke="#0d9d7c" strokeWidth="2.5" strokeLinejoin="round" />

      {/* Axis labels */}
      {REPORT_AXES.map((k, i) => {
        const { x, y } = axis(i);
        const lx = cx + x * labelR;
        const ly = cy + y * labelR;
        const isHighlight = highlight.includes(k);
        const anchor = Math.abs(x) < 0.2 ? 'middle' : x > 0 ? 'start' : 'end';
        return (
          <text
            key={k}
            x={lx}
            y={ly}
            textAnchor={anchor}
            dominantBaseline="middle"
            fontSize={isHighlight ? 13 : 11}
            fontWeight={isHighlight ? 800 : 600}
            fill={isHighlight ? '#0f7e64' : '#475467'}
            fontFamily="Inter, sans-serif"
          >
            {LABELS[k]}
          </text>
        );
      })}
    </svg>
  );
}
